"use client";

import React, { useEffect, useState } from "react";
import { ArrowRight, CircleCheck, Smartphone, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function TheDigitalJobsite() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return <section data-section-id="41287" id="structural-deck-expertise" ref={ref} className="bg-background text-foreground relative min-h-[95vh] flex items-center overflow-hidden py-20 lg:py-0">
      {}
      <div className="absolute inset-0 pointer-events-none z-0">
        {}
        {[10, 30, 50, 70, 90].map((top, i) => <div key={`h-${i}`} data-index={i} className={cn("absolute left-0 w-full h-[1px] bg-secondary/10 transition-all duration-[2000ms] ease-out", isVisible ? "w-full opacity-100" : "w-0 opacity-0")} style={{
        top: `${top}%`,
        transitionDelay: `${i * 150}ms`
      }} />)}
        {}
        {[10, 30, 50, 70, 90].map((left, i) => <div key={`v-${i}`} data-index={i} className={cn("absolute top-0 h-full w-[1px] bg-secondary/10 transition-all duration-[2000ms] ease-out", isVisible ? "h-full opacity-100" : "h-0 opacity-0")} style={{
        left: `${left}%`,
        transitionDelay: `${i * 150 + 500}ms`
      }} />)}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {}
          <div className="flex flex-col gap-8 max-w-2xl">
            <div className="space-y-2">
              <div className={cn("flex items-center gap-2 text-chart-1 font-mono text-sm tracking-widest uppercase transition-all duration-1000 delay-300", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4")}>
                <span className="h-[1px] w-8 bg-chart-1 inline-block" />
                PCH BUILDERS | EST. 1990
              </div>
              <h1 className={cn("text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-default font-bold leading-[0.9] tracking-tighter text-secondary transition-all duration-1000 delay-500", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
                STRUCTURAL <br />
                <span className="text-transparent bg-clip-text bg-linear-to-r from-secondary to-chart-1">
                  & CONCRETE
                </span>{" "}
                <br />
                DECK <br />
                SPECIALISTS
              </h1>
            </div>

            <p className={cn("text-lg text-muted-foreground max-w-lg border-l-2 border-chart-1/50 pl-6 font-default transition-all duration-1000 delay-700", isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4")}>
              Engineered for Safety. Built for Life. PCH Builders delivers specialized hillside engineering and reinforced concrete deck solutions for California homeowners since 1990.
            </p>

            <div className={cn("flex flex-wrap gap-4 pt-4 transition-all duration-1000 delay-900", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4")}>
              <Button size="lg" className="h-14 px-8 text-lg rounded-sm bg-chart-1 hover:bg-chart-1/90 text-white" asChild>
                <Link to="#">
                  Request Site Visit <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" className="h-14 px-8 text-lg rounded-sm border-secondary/20 text-secondary hover:bg-secondary/10" asChild>
                <Link to="#">View Engineering Specs</Link>
              </Button>
            </div>
          </div>

          {}
          <div className={cn("relative h-[500px] lg:h-[700px] w-full transition-all duration-1000 delay-500", isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95")}>
            {}
            <div className="absolute inset-0 bg-muted overflow-hidden border border-border group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1631011/img/modern_construction_site.png" alt="Structural hillside deck framing and concrete foundation work" className="w-full h-full object-cover grayscale contrast-125 transition-transform duration-700 group-hover:scale-105" />
              {}
              <div className="absolute inset-0 bg-secondary/20 mix-blend-hard-light pointer-events-none" />
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/graphy.png')] opacity-30 mix-blend-overlay pointer-events-none" />
              
              {}
              <div className="absolute top-10 left-10 w-4 h-4 border-l border-t border-chart-1/50" />
              <div className="absolute top-10 right-10 w-4 h-4 border-r border-t border-chart-1/50" />
              <div className="absolute bottom-10 left-10 w-4 h-4 border-l border-b border-chart-1/50" />
              <div className="absolute bottom-10 right-10 w-4 h-4 border-r border-b border-chart-1/50" />
            </div>

            {}
            <div className={cn("absolute -bottom-6 -left-6 lg:bottom-12 lg:-left-12 z-20 transition-all duration-1000 delay-1000", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
              <Card className="w-72 bg-card/95 backdrop-blur-sm border-chart-1/30 shadow-2xl rounded-sm">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between border-b border-border pb-4">
                    <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                      License Status
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-chart-3/40 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-chart-3"></span>
                      </span>
                      <span className="text-xs font-bold text-chart-3">
                        ACTIVE #583344
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-chart-1/10 text-chart-1">
                        <Activity className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold font-mono leading-none text-secondary">
                          30+
                        </p>
                        <p className="text-xs text-muted-foreground uppercase mt-1">
                          Years in CA
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-chart-1/10 text-chart-1">
                        <Smartphone className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-lg font-bold leading-none text-secondary">
                          Licensed
                        </p>
                        <p className="text-xs text-muted-foreground uppercase mt-1">
                          Bonded & Insured
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                     <div className="h-1 w-full bg-muted overflow-hidden">
                        <div className="h-full bg-chart-1 w-[99%] animate-pulse" />
                     </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}